import { useEffect, useRef, useState } from 'react';
import './TimeTravelOverlay.css';

/**
 * Full-screen sequence shown while /zenith/calculate is in flight.
 *
 * The wait is real and unavoidable — the Fly backend cold-wakes in ~7.7s and the
 * Gaia TAP query takes seconds more — so this occupies it rather than hiding it.
 * The readouts are the user's ACTUAL birth data, not decoration: a pre-rendered
 * video looks the same for everyone, the numbers are what make it theirs.
 *
 * Video source is swappable. Until the bespoke time-travel clip is delivered it
 * falls back to the hero footage, and if neither loads the gradient alone still
 * reads correctly — nothing here depends on the video existing.
 */

const PRIMARY_SRC = '/media/zenith-timetravel.mp4';
const FALLBACK_SRC = '/media/zenith-hero.mp4';

/** How long the year counter takes to sweep back, in ms. */
const REWIND_MS = 4200;

const STATUS_LINES = [
  'Locking birth coordinates',
  'Computing local sidereal time',
  'Resolving the zenith point',
  'Querying Gaia DR3',
  'Rewinding proper motion',
];

const DAY_MS = 86_400_000;
const YEAR_MS = 365.2425 * DAY_MS;

/**
 * Pick the unit the rewind counts in.
 *
 * Counting years is meaningless for a recent birth — a baby born three months
 * ago would watch "2026" tick to "2026". So the resolution follows the distance
 * actually travelled: years for a lifetime, months within a couple of years,
 * days within a couple of months.
 */
function resolutionFor(elapsedMs) {
  if (elapsedMs >= 3 * YEAR_MS) return 'year';
  if (elapsedMs >= 45 * DAY_MS) return 'month';
  return 'day';
}

function formatAt(ms, resolution) {
  const d = new Date(ms);
  if (resolution === 'year') return String(d.getFullYear());
  // Month range shows the DAY too. "Jun 2026" barely changes across a year's
  // rewind, so it reads as static; ticking the day turns the same sweep into
  // visible motion that decelerates onto the exact birth date.
  if (resolution === 'month') {
    return d.toLocaleDateString(undefined, {
      day: 'numeric', month: 'short', year: 'numeric',
    });
  }
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}

/**
 * Minimum time the sequence stays on screen, slightly longer than the rewind so
 * the animation always finishes.
 *
 * Without this the reveal is a lottery: a cold backend takes ~7.7s and you see
 * the whole journey, but a warm one answers in half a second and the overlay
 * flashes and vanishes. The moment someone travels back to their birth is the
 * emotional centre of the product — it shouldn't depend on whether a machine
 * happened to be awake.
 */
const MIN_VISIBLE_MS = REWIND_MS + 400;

export default function TimeTravelOverlay({ active, birthDate, latitude, longitude, onDone }) {
  const [src, setSrc] = useState(PRIMARY_SRC);
  const [label, setLabel] = useState(() => String(new Date().getFullYear()));
  const [status, setStatus] = useState(0);
  const [visible, setVisible] = useState(false);
  const rafRef = useRef(null);
  const shownAtRef = useRef(0);

  // Hold the overlay open until the rewind has had time to play out.
  useEffect(() => {
    if (active) {
      shownAtRef.current = Date.now();
      setVisible(true);
      return;
    }
    if (!shownAtRef.current) return;
    const remaining = MIN_VISIBLE_MS - (Date.now() - shownAtRef.current);
    if (remaining <= 0) {
      setVisible(false);
      onDone?.();
      return;
    }
    const id = setTimeout(() => { setVisible(false); onDone?.(); }, remaining);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  const targetMs = birthDate instanceof Date && !isNaN(birthDate)
    ? birthDate.getTime()
    : null;

  // Sweep backwards along the real timeline from now to the birth moment,
  // easing out so it decelerates into place rather than stopping dead. One
  // interpolation over timestamps, rendered at whichever resolution fits — so
  // a 50-year rewind and a 3-week one both read as travel.
  useEffect(() => {
    if (!visible || targetMs == null) return;

    const from = Date.now();
    const resolution = resolutionFor(Math.max(0, from - targetMs));
    const start = performance.now();

    const tick = (now) => {
      const t = Math.min((now - start) / REWIND_MS, 1);
      const eased = 1 - Math.pow(1 - t, 3);
      setLabel(formatAt(from + (targetMs - from) * eased, resolution));
      if (t < 1) rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [visible, targetMs]);

  // Cycle the status line. Purely cosmetic pacing — the real request gives no
  // progress events, so this must never claim a step has actually completed.
  useEffect(() => {
    if (!visible) return;
    setStatus(0);
    const id = setInterval(
      () => setStatus((s) => Math.min(s + 1, STATUS_LINES.length - 1)),
      1600
    );
    return () => clearInterval(id);
  }, [visible]);

  useEffect(() => {
    if (!visible) {
      setLabel(String(new Date().getFullYear()));
      setSrc(PRIMARY_SRC);
    }
  }, [visible]);

  if (!visible) return null;

  const coords =
    latitude !== '' && longitude !== '' && latitude != null && longitude != null
      ? `${Number(latitude).toFixed(4)}°, ${Number(longitude).toFixed(4)}°`
      : null;

  return (
    <div className="tt" role="status" aria-live="polite">
      <video
        className="tt-video"
        key={src}
        src={src}
        poster="/media/zenith-timetravel-poster.jpg"
        autoPlay
        muted
        loop
        playsInline
        aria-hidden="true"
        onError={() => setSrc((s) => (s === PRIMARY_SRC ? FALLBACK_SRC : s))}
      />
      <div className="tt-scrim" />

      <div className="tt-inner">
        <span className="tt-label">Rewinding to</span>

        <div className={`tt-year${label.length > 5 ? " is-long" : ""}`} aria-hidden="true">{label}</div>

        {coords && <div className="tt-coords">{coords}</div>}

        <div className="tt-rule" />

        <p className="tt-status">{STATUS_LINES[status]}&hellip;</p>

        {/* The visible year is animated, so give assistive tech the real target
            once rather than a counter that changes sixty times a second. */}
        <span className="tt-sr">
          Calculating your star band
          {targetMs ? ` for ${new Date(targetMs).toLocaleDateString()}` : ''}. This takes a few seconds.
        </span>
      </div>
    </div>
  );
}
